import { Label, assets } from '@round/whitelabel-ui';
import styles from '../SongFormAudios.module.css';
import { useState } from 'react';
import { SongFormValues } from '../../SongForm';
import { useFormikContext } from 'formik';
import { showNotification } from 'helpers';
import { Button } from '@round/whitelabel-ui';
import { Input } from '@round/whitelabel-ui';
import InstagramAudiosTable, { InstagramAudiosTableRow } from './Table/InstagramAudiosTable';
import { useInstagramAudios } from '@round/platforms/instagram';
import { useFindOrCreateInstagramAudio } from 'Modules/Instagram/hooks/queries/useFindOrCreateInstagramAudio';
import uniq from 'lodash/uniq';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { instagramQueries } from '@round/api';

const { Plus: PlusIcon } = assets;

type Props = {
    staticInstagramAudios: number[];
};

const SongFormInstagramAudios = ({ staticInstagramAudios }: Props) => {
    const { values, setFieldValue } = useFormikContext<SongFormValues>();

    const ids = uniq([...staticInstagramAudios, ...values.instagram_audios]);
    const { data, isLoading, isError } = useInstagramAudios(
        { id: ids.join(','), page_size: ids.length },
        { enabled: ids.length > 0, placeholderData: keepPreviousData }
    );
    const audios = data?.results ?? [];

    const { findOrCreate, isLoading: isAddAudioLoading } = useFindOrCreateInstagramAudio();
    const [instagramAudioUrl, setInstagramAudioUrl] = useState('');
    const queryClient = useQueryClient();

    const handleAddAudio = async () => {
        if (!instagramAudioUrl) {
            return;
        }

        try {
            const audio = await findOrCreate(instagramAudioUrl);

            if ([...values.instagram_audios, ...(staticInstagramAudios || [])].includes(audio.id)) {
                showNotification('Audio already added', 'error');
                return;
            }

            // Seed the next list-query key with the new audio so the row renders
            // immediately instead of waiting for the keepPreviousData refetch.
            const newIds = uniq([...staticInstagramAudios, audio.id, ...values.instagram_audios]);
            const seededResults = [audio, ...audios];
            queryClient.setQueryData(
                instagramQueries.audio.keys.list({ id: newIds.join(','), page_size: newIds.length }),
                { count: seededResults.length, next: null, previous: null, results: seededResults }
            );

            setInstagramAudioUrl('');
            setFieldValue('instagram_audios', [audio.id, ...values.instagram_audios]);
            showNotification('Audio added successfully', 'info');
        } catch (e) {
            const message = e instanceof Error ? e.message : `Couldn't add audio`;
            showNotification(message, 'error');
        }
    };

    const handleRemoveAudio = (audioId: number) => {
        setFieldValue(
            'instagram_audios',
            values.instagram_audios.filter((id) => id !== audioId)
        );
    };

    const rows = (() => {
        let rows: InstagramAudiosTableRow[] = [];

        //if the song is changed to unreleased, we will hide the static audios
        if (values.status === 'released') {
            rows =
                staticInstagramAudios?.reduce<InstagramAudiosTableRow[]>((acc, id) => {
                    const audio = audios?.find((audio) => audio.id === id);
                    return audio ? acc.concat({ ...audio, isStatic: true }) : acc;
                }, []) ?? [];
        }

        return rows.concat(
            values.instagram_audios
                .filter((id) => !staticInstagramAudios?.includes(id))
                .reduce<InstagramAudiosTableRow[]>((acc, id) => {
                    const audio = audios?.find((audio) => audio.id === id);
                    return audio ? acc.concat({ ...audio, isStatic: false }) : acc;
                }, []) ?? []
        );
    })();

    return (
        <div className={styles.platformAudiosContainer}>
            <div className={styles.field}>
                <Label>Instagram Audio</Label>
                <div className={styles.audioInputRow}>
                    <Input
                        placeholder="Enter Instagram audio URL"
                        className={styles.audioInput}
                        value={instagramAudioUrl}
                        onChange={(e) => setInstagramAudioUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                void handleAddAudio();
                            }
                        }}
                        disabled={isError}
                    />
                    <Button
                        appearance="primary"
                        onClick={handleAddAudio}
                        disabled={isAddAudioLoading || isLoading || isError}
                        isLoading={isAddAudioLoading}
                        type="button"
                    >
                        <PlusIcon className={styles.plusIcon} />
                    </Button>
                </div>
            </div>

            <InstagramAudiosTable
                data={rows}
                isLoading={isLoading}
                onRemoveAudio={handleRemoveAudio}
                hasError={isError}
            />
        </div>
    );
};

export default SongFormInstagramAudios;
